import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import PressCarousel from '../components/PressCarousel';
import axios from 'axios';
import { API_URL as API } from '../utils/config';

const Home = () => {
  const { t, language } = useLanguage();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?featured=true`);
      setProducts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="bg-[#F9F9F7]" data-testid="home-page">
        <div className="w-full">
          <img 
            src="https://customer-assets.emergentagent.com/job_cosmobeauty-shop/artifacts/g97vgp5s_cosmo%20beauty%20first%20page%281%29.png" 
            alt="Cosmo Beauty Hero" 
            className="w-full h-auto"
          />
        </div>
        <div className="text-center px-6 max-w-4xl mx-auto py-12">
          <h1 className="text-sm font-sans tracking-tight leading-tight text-[#1A1A1A] mb-3 animate-fade-in-out">
            {language === 'no' ? 'Kåret til verdens beste foundation' : 'Awarded World\'s Best Foundation'}
          </h1>
          <p className="text-sm font-sans leading-relaxed tracking-wide text-[#8A8A8A] mb-8 animate-fade-in-out-delay">
            {language === 'no' ? '100% ren mineralmakeup fra Norge' : '100% Pure Mineral Makeup from Norway'}
          </p>
          <div className="flex gap-2 justify-center flex-wrap">
            <Link 
              to="/shop" 
              className="bg-[#1A1A1A] text-white px-2 py-1 text-xs uppercase tracking-[0.2em] hover:bg-[#C6A87C] transition-all duration-300"
              data-testid="hero-shop-now-btn"
            >
              {t('shopNow')}
            </Link>
            <Link 
              to="/about" 
              className="bg-white border-2 border-[#1A1A1A] text-[#1A1A1A] px-2 py-1 text-xs uppercase tracking-[0.2em] hover:bg-[#C6A87C] hover:text-white hover:border-[#C6A87C] transition-all duration-300"
              data-testid="hero-learn-more-btn"
            >
              {t('learnMore')}
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 md:py-32 bg-[#F9F9F7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <h2 className="text-lg font-sans font-bold uppercase tracking-tight text-[#1A1A1A] mb-6">
            {language === 'no' ? 'Mer enn bare makeup' : 'More than just makeup'}
          </h2>
          {language === 'no' ? (
            <div className="space-y-4 text-base md:text-lg leading-relaxed text-[#8A8A8A] max-w-3xl mx-auto">
              <p>COSMOBEAUTY er mer enn bare makeup, vi tilbyr også spa- og skjønnhetstjenester som ivaretar kundenes behov, fra hudpleie, hår, makeup og de nyeste trendene innen skjønnhet og SPA-behandlinger.</p>
              <p>Våre produkter er egnet for alle aldersgrupper, og vi er forpliktet til å gi våre kunder det høyeste nivået av kvalitet og kundeservice.</p>
            </div>
          ) : (
            <div className="space-y-4 text-base md:text-lg leading-relaxed text-[#8A8A8A] max-w-3xl mx-auto">
              <p>COSMOBEAUTY is more than just makeup, we also offer spa and beauty services that cater to the needs of our customers, from skincare, hair, makeup, and the latest trends in beauty and SPA treatments.</p>
              <p>Our products are suitable for all ages, and we are committed to providing our customers with the highest level of quality and customer service.</p>
            </div>
          )}
        </div>
      </section>

      {/* Press Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <h2 className="text-lg font-sans font-bold uppercase tracking-tight text-center text-[#1A1A1A] mb-4">
            {language === 'no' ? 'Presseomtale' : 'Press Coverage'}
          </h2>
          <p className="text-xs uppercase tracking-[0.2em] text-[#8A8A8A] text-center mb-12">
            {language === 'no' ? 'Som sett i' : 'As Seen In'}
          </p>
          <PressCarousel />
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <h2 className="text-lg font-sans font-bold uppercase tracking-tight text-center text-[#1A1A1A] mb-12">
            {language === 'no' ? 'Våre Produkter' : 'Our Products'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {products.map((product, index) => {
              const name = language === 'no' ? product.name_no : product.name_en;
              const shadeText = product.shades?.[0]?.name || '';
              return (
                <div key={product.id} className="text-center" data-testid={`featured-product-${index}`}>
                  <div className="bg-white p-3 mb-2 shadow-md rounded-lg flex items-center justify-center" style={{ height: '120px' }}>
                    <img 
                      src={product.image_url} 
                      alt={name} 
                      className="h-24 w-auto object-contain"
                    />
                  </div>
                  <h3 className="text-lg font-sans text-[#1A1A1A] mb-1">
                    {name}
                  </h3>
                  <p className="text-sm text-[#8A8A8A] mb-1">{shadeText}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#8A8A8A] mb-2">
                    {product.category}
                  </p>
                  <p className="text-base font-sans text-[#1A1A1A] mb-3">{product.price_nok} NOK</p>
                  <Link 
                    to={`/product/${product.id}`}
                    className="inline-block bg-[#1A1A1A] text-white px-5 py-2 text-xs uppercase tracking-[0.2em] hover:bg-[#333333] transition-all duration-300"
                  >
                    {t('shopNow')}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* YouTube Video Section */}
      <section className="w-full">
        <div className="relative w-full" style={{ paddingBottom: '56.25%', height: 0 }}>
          <iframe
            className="absolute top-0 left-0 w-full h-full"
            src="https://www.youtube.com/embed/FR9hVHF8Ocg?autoplay=1&mute=1&loop=1&playlist=FR9hVHF8Ocg&controls=0&modestbranding=1&showinfo=0&rel=0&iv_load_policy=3&disablekb=1"
            title="Cosmo Beauty Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default Home;